import { useEffect } from 'react';
import create from 'zustand';
import { useAuthentication } from '~/shared/hooks';
import { useBasket } from '~/components/features/Basket';
import { CommerceModel } from 'api-types';
import { LoginResponse } from 'api-types/src/data-contract/auto-generated/auth-model';

/**
 * Data structure injected into queued tracking events when they are resolved.
 * This ensures all tracking events have access to the current user state and basket data.
 */
type InjectedData = {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** The current basket/cart data, if available */
  basket?: CommerceModel.Basket;
  user?: LoginResponse;
};

type QueueItem = (injectedData: InjectedData) => void;

type QueueStore = {
  queue: QueueItem[];
  addToQueue: (item: QueueItem) => void;
  resolveQueue: (injectedData: InjectedData) => void;
};

const useQueueStore = create<QueueStore>((set) => ({
  queue: [],
  addToQueue: (item) => set((state) => ({ queue: [...state.queue, item] })),
  resolveQueue: (injectedData) => {
    set((state) => {

      state.queue.forEach((item) => {
        item(injectedData);
      });

      return { queue: [] };
    });
  },
}));

/**
 * GTM Queue system for handling tracking events with asynchronous data dependencies.
 * 
 * Events are queued when tracking components mount, then resolved with complete
 * context data (authentication state, basket data) when all dependencies are ready.
 * 
 * @returns Object with addToQueue function for queuing tracking events
 */
export const useGTMQueue = () => {
  const { queue, addToQueue, resolveQueue } = useQueueStore();
  const { isAuthenticated, isAuthenticating, session } = useAuthentication();
  const { data: basket, isLoading: isBasketLoading } = useBasket();

  // Only resolve queue when both authentication and basket data are ready
  const isTrackingReady = !isAuthenticating && !isBasketLoading;

  useEffect(() => {
    if (isTrackingReady && queue.length) {
      resolveQueue({ isAuthenticated, basket, user: session?.user });
    }
  }, [isTrackingReady, queue, isAuthenticated, basket]);

  return { addToQueue };
};
